import React from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { Button, Row, Col, Form as BsForm } from 'react-bootstrap';
import SeletorGrupoEmpresa from '~/Components/SeletorGrupoEmpresa';

const normalizarCgcCpf = (valor) => {
  if (!valor) return undefined;
  const documento = String(valor).toUpperCase().replace(/[^A-Z0-9]/g, '');
  if (documento === '') return undefined;
  if (/[A-Z]/.test(documento) || documento.length > 11) return documento;
  return String(valor).replace(/\D/g, '') || undefined;
};

const numeroOpcional = () =>
  Yup.number()
    .transform((value, originalValue) =>
      originalValue === '' || originalValue == null ? null : value
    )
    .nullable();

const dataOpcional = () =>
  Yup.date()
    .transform((value, originalValue) =>
      originalValue === '' || originalValue == null ? null : value
    )
    .nullable();

const schema = Yup.object().shape({
  nossoNumero: Yup.string()
    .nullable()
    .test('tamanho', 'Nosso Nº inválido', (val) => {
      if (!val || val.trim() === '') return true;
      return /^\d{15,}$/.test(val.trim());
    }),
  codigoTitulo: numeroOpcional().integer().positive(),
  bancoCheque: numeroOpcional().integer().positive(),
  agenciaCheque: numeroOpcional().integer().positive(),
  contaCheque: numeroOpcional().integer().positive(),
  numeroCheque: numeroOpcional().integer().positive(),
  numeroDuplicata: Yup.string().nullable(),
  cedente: numeroOpcional().integer().positive(),
  portador: numeroOpcional().integer().positive(),
  vencimentoInicial: dataOpcional(),
  vencimentoFinal: dataOpcional()
    .when('vencimentoInicial', (vencimentoInicial, sch) =>
      vencimentoInicial
        ? sch.min(vencimentoInicial, 'Vencimento final deve ser ≥ inicial')
        : sch
    ),
  valorInicial: numeroOpcional().min(0),
  valorFinal: numeroOpcional().min(0),
  codigoBoleto: numeroOpcional().integer().positive(),
  parcela: numeroOpcional().integer().positive(),
});

const valoresIniciais = {
  nossoNumero: '',
  codigoTitulo: '',
  cgcCpf: '',
  bancoCheque: '',
  agenciaCheque: '',
  contaCheque: '',
  numeroCheque: '',
  numeroDuplicata: '',
  cedente: '',
  portador: '',
  vencimentoInicial: '',
  vencimentoFinal: '',
  valorInicial: '',
  valorFinal: '',
  codigoBoleto: '',
  parcela: '',
};

const FiltrosBusca = ({ grupoEmpresa, onGrupoEmpresaChange, onBuscar, carregando }) => (
  <Formik
    initialValues={valoresIniciais}
    validationSchema={schema}
    onSubmit={(values) => {
      if (!grupoEmpresa) {
        return;
      }
      const filtro = {
        codigo_grupo_empresa: grupoEmpresa.value,
        nosso_numero: values.nossoNumero || undefined,
        codigo_titulo: values.codigoTitulo || undefined,
        cgc_cpf: normalizarCgcCpf(values.cgcCpf),
        banco_cheque: values.bancoCheque || undefined,
        agencia_cheque: values.agenciaCheque || undefined,
        conta_cheque: values.contaCheque || undefined,
        numero_cheque: values.numeroCheque || undefined,
        numero_duplicata: values.numeroDuplicata || undefined,
        conta_cedente: values.cedente || undefined,
        conta_portador: values.portador || undefined,
        vencimento_inicial: values.vencimentoInicial || undefined,
        vencimento_final: values.vencimentoFinal || undefined,
        valor_inicial: values.valorInicial || undefined,
        valor_final: values.valorFinal || undefined,
        codigo_boleto: values.codigoBoleto || undefined,
        parcela: values.parcela || undefined,
        pagina: 1,
        tamanho_pagina: 20,
        somente_manutencao: true,
      };
      onBuscar(filtro);
    }}
  >
    {({ handleReset }) => (
      <Form>
        <Row className="mb-2">
          <Col md={4}>
            <BsForm.Label className="font-weight-bold">Grupo de Empresas *</BsForm.Label>
            <SeletorGrupoEmpresa
              value={grupoEmpresa}
              onChange={onGrupoEmpresaChange}
              disabled={carregando}
            />
            {!grupoEmpresa && (
              <div className="text-danger small mt-1">Escolha o Grupo de empresas.</div>
            )}
          </Col>
          <Col md={4}>
            <BsForm.Label>Nosso Número</BsForm.Label>
            <Field name="nossoNumero" className="form-control" placeholder="≥ 15 dígitos" />
            <ErrorMessage name="nossoNumero" component="div" className="text-danger small" />
          </Col>
          <Col md={2}>
            <BsForm.Label>Código Título</BsForm.Label>
            <Field name="codigoTitulo" type="number" min="1" className="form-control" />
          </Col>
          <Col md={2}>
            <BsForm.Label>CPF / CNPJ</BsForm.Label>
            <Field name="cgcCpf" className="form-control" />
          </Col>
        </Row>

        <Row className="mb-2">
          <Col md={2}>
            <BsForm.Label>Banco</BsForm.Label>
            <Field name="bancoCheque" type="number" min="1" className="form-control" />
            <ErrorMessage name="bancoCheque" component="div" className="text-danger small" />
          </Col>
          <Col md={2}>
            <BsForm.Label>Agência</BsForm.Label>
            <Field name="agenciaCheque" type="number" min="1" className="form-control" />
            <ErrorMessage name="agenciaCheque" component="div" className="text-danger small" />
          </Col>
          <Col md={2}>
            <BsForm.Label>Conta</BsForm.Label>
            <Field name="contaCheque" type="number" min="1" className="form-control" />
          </Col>
          <Col md={2}>
            <BsForm.Label>Nº Cheque</BsForm.Label>
            <Field name="numeroCheque" type="number" min="1" className="form-control" />
          </Col>
          <Col md={2}>
            <BsForm.Label>Nº Duplicata</BsForm.Label>
            <Field name="numeroDuplicata" className="form-control" />
          </Col>
          <Col md={2}>
            <BsForm.Label>Parcela</BsForm.Label>
            <Field name="parcela" type="number" min="1" className="form-control" />
            <ErrorMessage name="parcela" component="div" className="text-danger small" />
          </Col>
        </Row>

        <Row className="mb-2">
          <Col md={2}>
            <BsForm.Label>Cód. Boleto</BsForm.Label>
            <Field name="codigoBoleto" type="number" min="1" className="form-control" />
            <ErrorMessage name="codigoBoleto" component="div" className="text-danger small" />
          </Col>
          <Col md={2}>
            <BsForm.Label>Conta Cedente</BsForm.Label>
            <Field name="cedente" type="number" min="1" className="form-control" />
          </Col>
          <Col md={2}>
            <BsForm.Label>Conta Portador</BsForm.Label>
            <Field name="portador" type="number" min="1" className="form-control" />
          </Col>
          <Col md={2}>
            <BsForm.Label>Vencimento inicial</BsForm.Label>
            <Field name="vencimentoInicial" type="date" className="form-control" />
            <ErrorMessage name="vencimentoInicial" component="div" className="text-danger small" />
          </Col>
          <Col md={2}>
            <BsForm.Label>Vencimento final</BsForm.Label>
            <Field name="vencimentoFinal" type="date" className="form-control" />
            <ErrorMessage name="vencimentoFinal" component="div" className="text-danger small" />
          </Col>
          <Col md={2}>
            <BsForm.Label>Valor de</BsForm.Label>
            <Field name="valorInicial" type="number" min="0" step="0.01" className="form-control" placeholder="0,00" />
          </Col>
        </Row>

        <Row className="mb-3">
          <Col md={2}>
            <BsForm.Label>Valor até</BsForm.Label>
            <Field name="valorFinal" type="number" min="0" step="0.01" className="form-control" placeholder="0,00" />
          </Col>
          <Col md={10} className="d-flex align-items-end">
            <Button
              type="submit"
              variant="primary"
              disabled={carregando || !grupoEmpresa}
              className="mr-2"
            >
              {carregando ? 'Buscando...' : 'Buscar'}
            </Button>
            <Button
              type="button"
              variant="outline-secondary"
              disabled={carregando}
              onClick={handleReset}
            >
              Limpar
            </Button>
          </Col>
        </Row>
      </Form>
    )}
  </Formik>
);

export default FiltrosBusca;
